import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useMutation } from '@tanstack/react-query'
import { useEventListener } from 'usehooks-ts'

import { useMe } from '@shared/providers/src/MeProvider'
import API from '@shared/services/src/API'

import { useNotificationRedirect } from '@pages/Notifications/Notifications.hooks'

const SERVICE_WORKER_URL = '/service-worker.js'
const ENABLED_USERS_KEY = 'web-push-enabled-users'

export const isWebPushSupported = () =>
  Boolean(import.meta.env.VITE_VAPID_PUBLIC_KEY) && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

const toApplicationServerKey = (key) => {
  const base64 = key.replace(/=+$/, '').replace(/-/g, '+').replace(/_/g, '/')
  return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))
}

const getEnabledUsers = () => JSON.parse(localStorage.getItem(ENABLED_USERS_KEY) ?? '[]')

const setEnabled = (userId, enabled) => {
  const users = getEnabledUsers().filter((id) => id !== userId)
  localStorage.setItem(ENABLED_USERS_KEY, JSON.stringify(enabled ? [...users, userId] : users))
}

const createSubscription = (registration) =>
  registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: toApplicationServerKey(import.meta.env.VITE_VAPID_PUBLIC_KEY),
  })

const getSubscription = () =>
  navigator.serviceWorker.getRegistration().then((registration) => registration?.pushManager.getSubscription() ?? null)

const saveSubscription = (userId, subscription) => {
  const { endpoint, keys } = subscription.toJSON()
  return API.users.id(userId).webPushSubscriptions.create({ endpoint, p256dh: keys.p256dh, auth: keys.auth })
}

const closeNotifications = () => {
  if (!isWebPushSupported() || document.visibilityState !== 'visible') return

  navigator.serviceWorker
    .getRegistration()
    .then((registration) => registration?.getNotifications())
    .then((notifications) => notifications?.forEach((notification) => notification.close()))
}

export default function useWebPush() {
  const me = useMe()
  const supported = isWebPushSupported()

  const [permission, setPermission] = useState(supported ? Notification.permission : 'denied')
  const [subscription, setSubscription] = useState(null)

  useEffect(() => {
    if (!supported) return
    getSubscription().then(setSubscription)
  }, [supported])

  const subscribe = useMutation({
    mutationFn: async () => {
      const result = await Notification.requestPermission()
      setPermission(result)
      if (result !== 'granted') return null

      const registration = await navigator.serviceWorker.register(SERVICE_WORKER_URL)
      await navigator.serviceWorker.ready
      const created = await createSubscription(registration).catch(() => null)
      if (!created) {
        toast.error('Could not turn on browser notifications. Restart the browser and try again')
        return null
      }

      await saveSubscription(me.id, created)
      setEnabled(me.id, true)
      return created
    },
    onSuccess: (created) => created && setSubscription(created),
  })

  const unsubscribe = useMutation({
    mutationFn: () => getSubscription().then((current) => current?.unsubscribe()),
    onSuccess: () => {
      setEnabled(me.id, false)
      setSubscription(null)
    },
  })

  return {
    supported,
    permission,
    subscribed: Boolean(subscription) || (permission === 'granted' && getEnabledUsers().includes(me.id)),
    isPending: subscribe.isPending || unsubscribe.isPending,
    subscribe: subscribe.mutateAsync,
    unsubscribe: unsubscribe.mutateAsync,
  }
}

export function useWebPushListener() {
  const me = useMe()
  const redirect = useNotificationRedirect()

  const supported = isWebPushSupported()
  const serviceWorker = useRef(supported ? navigator.serviceWorker : null)

  useEffect(() => {
    if (!supported || Notification.permission !== 'granted') return

    navigator.serviceWorker.register(SERVICE_WORKER_URL).then(async (registration) => {
      const existing = await registration.pushManager.getSubscription()
      const restored = !existing && getEnabledUsers().includes(me.id)
      const subscription = restored ? await createSubscription(registration).catch(() => null) : existing
      if (!subscription) return

      setEnabled(me.id, true)
      saveSubscription(me.id, subscription)
    })
  }, [me.id, supported])

  useEventListener(
    'message',
    ({ data }) => {
      if (data?.type === 'web-push-click') redirect(data.path)
    },
    serviceWorker
  )

  useEffect(closeNotifications, [])
  useEventListener('visibilitychange', closeNotifications)
}
